import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-react-intl';
import ReactHtmlParser from 'react-html-parser';
import { BLOCKS } from '@contentful/rich-text-types';

import Protip from '../../../ProTip';
import ToolTip from '../../../../Core/ToolTip';
import RichText from '../../../../Core/RichText';
import Input from '../../../../Core/Input';
import Select from '../../../../Core/Select';

import {
  BankingContainer,
  InputContainer,
  InputWrapper,
  InputTitle,
  InputBody,
  StyledImage,
  Currency,
  InputComponent,
  Legend,
} from './styles';

const header = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Legend>{children}</Legend>,
    [BLOCKS.HEADING_1]: (_, children) => <Legend>{children}</Legend>,
    [BLOCKS.HEADING_2]: (_, children) => <Legend>{children}</Legend>,
    [BLOCKS.HEADING_3]: (_, children) => <Legend>{children}</Legend>,
  },
};

const CalculatorBanking = ({
  data,
  setEstimatedBanking,
  calculatorValues,
  setCalculatorValues,
  selectedOption,
  setSelectedOption,
  dropdownOptions,
  heading = null,
}) => {
  if (!data) return null;

  const intl = useIntl();
  const { sliderSection, proTip } = data;

  const bankingSliderFields = sliderSection.map(slider => {
    if (slider.inputType === 'dropdown' || slider.inputType === 'input') {
      return slider.sliderLabel.split(' ').join('-').toLocaleLowerCase();
    }
    return null;
  });

  const pointsCalculator = pts => {
    if (pts < 1500) {
      return 0;
    } else if (pts > 1499 && pts < 3000) {
      return 2000;
    } else if (pts > 2999) {
      return 5000;
    }
    return 0;
  };

  // * This func updates the state value for 1 input. It requires a 'state key', which is the field name of the input
  const updateInputValue = (stateKey, price) => {
    setCalculatorValues(prevState => {
      const newState = {};
      const pointValue = pointsCalculator(price);
      const innerState = { value: price, points: pointValue };
      newState[stateKey] = { ...prevState[stateKey], ...innerState };
      return { ...prevState, ...newState };
    });
  };

  // * This func updates the state value for 1 input. It requires a 'state key', which is the field name of the input
  const updateDropdownValue = stateKey => {
    setCalculatorValues(prevState => {
      const newState = {};
      const pointValue = selectedOption
        ? parseInt(selectedOption.value, 10)
        : 0;
      const innerState = { points: pointValue };
      newState[stateKey] = { ...prevState[stateKey], ...innerState };
      return { ...prevState, ...newState };
    });
  };

  const calculate = () => {
    // * Reset the purchases and points to 0, so we dont need to deal with negatives
    let totalPoints = 0;
    // filter out total monthly spending object inside the slidervalue object
    const keys = Object.keys(calculatorValues).filter(key =>
      bankingSliderFields.includes(key),
    );
    // * Total up all the purchases and the points of each slider
    keys.forEach(key => {
      if (calculatorValues[key].value) {
        totalPoints += pointsCalculator(calculatorValues[key].value);
      } else {
        totalPoints += calculatorValues[key].points;
      }
    });
    // * Set the price total in state
    setEstimatedBanking(totalPoints);
  };

  useEffect(() => {
    calculate();
  }, [calculatorValues]);

  useEffect(() => {
    const dropdownSlider = sliderSection.find(
      slider => slider.inputType === 'dropdown',
    );
    updateDropdownValue(
      dropdownSlider.sliderLabel.split(' ').join('-').toLocaleLowerCase(),
    );
  }, [selectedOption]);

  const componentResolver = input => {
    const stateKey = input.sliderLabel.split(' ').join('-').toLocaleLowerCase();
    switch (input.inputType) {
      case 'input':
        return (
          <InputComponent>
            <Currency locale={intl.locale}>
              <Input
                dataInputName={input.sliderLabel}
                state={calculatorValues[stateKey].value}
                stateKey={stateKey}
                onChange={updateInputValue}
                min={0}
                label={input.sliderLabel}
                arialabel={`${intl.formatMessage({
                  id: 'monthlyUsage',
                })} ${input.sliderLabel}`}
              />
            </Currency>
          </InputComponent>
        );

      case 'dropdown': {
        return (
          <InputComponent>
            <Select
              placeholder={`${intl.formatMessage({
                id: 'selectDefault',
              })}`}
              className="calculator"
              dataSelectName={input.sliderLabel}
              options={[...dropdownOptions]}
              state={selectedOption}
              setState={setSelectedOption}
              displayLabel="display"
              labelTitle={input.toolTipAriaLabel || ''}
            />
          </InputComponent>
        );
      }
      default:
        return null;
    }
  };

  return (
    <BankingContainer>
      {heading && <RichText document={heading} options={header} />}
      {sliderSection &&
        sliderSection.map(inputs => {
          return (
            <InputContainer key={inputs.id}>
              <InputWrapper>
                <InputTitle>
                  {inputs.sliderIconDam && (
                    <StyledImage src={inputs.sliderIconDam} alt="" />
                  )}
                  <p>{ReactHtmlParser(inputs.sliderLabel)}</p>
                  {inputs.sliderTooltip && (
                    <ToolTip
                      id={inputs.id}
                      tipData={inputs.sliderTooltip}
                      labelId={inputs.sliderLabel}
                      ariaLabel={`${intl.formatMessage({
                        id: 'toolTip',
                      })} ${inputs.sliderLabel}: ${inputs.sliderTooltip}.`}
                    >
                      ?
                    </ToolTip>
                  )}
                </InputTitle>
                <InputBody>
                  <RichText document={inputs.bodyCopy} />
                </InputBody>
              </InputWrapper>
              {componentResolver(inputs)}
            </InputContainer>
          );
        })}
      {proTip && (
        <Protip className="internal calculator" key={proTip.id} data={proTip} />
      )}
    </BankingContainer>
  );
};

CalculatorBanking.propTypes = {
  data: PropTypes.shape().isRequired,
  setEstimatedBanking: PropTypes.func.isRequired,
  calculatorValues: PropTypes.shape().isRequired,
  setCalculatorValues: PropTypes.func.isRequired,
  selectedOption: PropTypes.shape().isRequired,
  setSelectedOption: PropTypes.func.isRequired,
  dropdownOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  heading: PropTypes.shape(),
};

export default CalculatorBanking;
